import * as Sentry from '@sentry/react';
import { GraphQLError } from 'graphql';
import { History } from 'history';
import _ from 'lodash';
import { DateTime } from 'luxon';

import {
  date as commonDate,
  error as commonError,
  finleyAssert,
  url as commonUrl,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  DeliverablesPayloadFragmentGQLType,
  EventForRenderFragmentGQLType,
  FundingRequestComponentPayloadFragmentGQLType,
  FundingRequestPayloadFragmentGQLType,
  IngestionPayloadFragmentGQLType,
  InsertedDeliverablePayloadFragmentGQLType,
  Maybe,
  SubmittedDeliverableSubmissionPayloadFragmentGQLType,
  UpdatedDeliverableStatusPayloadFragmentGQLType,
  VerificationPayloadFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { Bank } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const { isNil, isNumber, round } = _;

// TODO: All of these methods should be consoildates with common-modules

/**
 * Convert a number to one with commas, e.g. 12345 => 12,345
 * @param number number, can be either string or number
 */
export const numberWithCommas = (number?: string | number) => {
  if (isNil(number)) {
    return number;
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Convert a number to one with commas, e.g. 12345 => $12,345
 * @param number number, can be either string or number
 */

// TODO: make it generic for other currencies and get rid of mxnNumberWithCommas.
export const dollarNumberWithCommas = (
  number?: string | number,
  fixedPoint: number = 2,
) => {
  if (isNil(number) || !isNumber(number)) {
    return number;
  }
  const dollarVal = round(Math.abs(Number(number)), 2).toFixed(fixedPoint);
  if (number === 0) {
    return '$0';
  }
  if (number < 0) {
    return `-$${numberWithCommas(dollarVal)}`;
  }
  return `$${numberWithCommas(dollarVal)}`;
};

export const mxnNumberWithCommas = (
  number?: string | number,
  fixedPoint: number = 2,
) => {
  if (isNil(number) || !isNumber(number)) {
    return number;
  }
  const dollarVal = round(Math.abs(Number(number)), 2).toFixed(fixedPoint);
  if (number === 0) {
    return 'MX $0';
  }
  if (number < 0) {
    return `-MX $${numberWithCommas(dollarVal)}`;
  }
  return `MX $${numberWithCommas(dollarVal)}`;
};

/**
 * Converts a number to one with dollar and commas, and displays 0 as '-'
 * Unlike `dollarNumberWithCommas` which returns 0 as $0
 */
export const dollarNumberWithCommasOrDash = (value: number) =>
  value === 0 ? '-' : dollarNumberWithCommas(value);

/**
 * Convert a number to one that's formatted, e.g. 20000 => 20K and 5000000 to 5M
 * @param number number to be formatted
 */
export const numberToFormattedRounded = (number?: number): Maybe<string> => {
  if (isNil(number)) {
    return null;
  }
  const absNumber = Math.abs(number);
  let formattedNumber = `${Number(absNumber.toFixed(2)) * 1}`; // multiplying by 1 to remove trailing zeroes
  if (absNumber >= 1000000000) {
    formattedNumber = `$${(absNumber / 1000000000)
      .toFixed(1)
      .replace(/\.0$/, '')}G`;
  } else if (absNumber >= 1000000) {
    formattedNumber = `${(absNumber / 1000000)
      .toFixed(1)
      .replace(/\.0$/, '')}M`;
  } else if (absNumber >= 1000) {
    formattedNumber = `${(absNumber / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  if (number < 0) {
    return `-${formattedNumber}`;
  }
  return formattedNumber;
};

/**
 * Convert a number to one that's formatted, e.g. 20000 => $20K and 5000000 to $5M
 * @param number number to be formatted
 */
export const numberToDollarFormattedRounded = (
  number?: number,
): Maybe<string> => {
  const formattedRoundedNumber = numberToFormattedRounded(number);
  if (isNil(formattedRoundedNumber)) {
    return null;
  }
  if (formattedRoundedNumber.slice(0, 1) === '-') {
    return `-$${formattedRoundedNumber.slice(1)}`;
  }
  return `$${formattedRoundedNumber}`;
};

/**
 *
 * @param fileName e.g. borrowing-base-certificate-2021-06-07.csv
 * @returns e.g. borrowing-ba...06-07.xlsx
 */
export const truncateFileName = (fileName: string): string => {
  // no need to truncate
  if (fileName.length <= 24) {
    return fileName;
  }
  const regEx = /^([a-zA-Z-]*)-[\d]{4}-([\d]{2}-[\d]{2}\..*)$/;
  const fileMatch = fileName.match(regEx);
  if (fileMatch) {
    const [, namePrefix, nameSuffix] = fileMatch; // e.g. 'borrowing-base-certificate', '06-07.xlsx'
    const truncatedNamePrefix = namePrefix.substring(0, 12);
    return `${truncatedNamePrefix}...${nameSuffix}`;
  }
  return fileName;
};

/**
 * Convert a number to percentage label. e.g. 0.0124 => "1.24%"
 * @param number decimal number
 * @param scale the number of digits past the decimal point
 * @returns percentage value string
 */
export const numberToPercentageLabel = (
  number: Maybe<number>,
  scale = 3,
): Maybe<string> => {
  if (isNil(number)) {
    return null;
  }
  return `${round((number as number) * 100, scale)}%`;
};

/**
 * Convert a number to percentage label. e.g. 1.24 => "1.24%"
 * @param number decimal number
 * @param scale the number of digits past the decimal point
 * @returns percentage value string
 */
export const percentageToPercentageLabel = (
  number: Maybe<number>,
  scale = 3,
): Maybe<string> => {
  if (isNil(number)) {
    return null;
  }
  return `${round(number as number, scale)}%`;
};

// custom hook to log out on auth error
// used in all onError apollo queries
export const logOutOnAuthErrors = (
  graphQLErrors: readonly GraphQLError[],
  removeAuthToken: Function,
  history: History,
) => {
  const unauthenticatedErr = graphQLErrors.find((err) => {
    return err.extensions && err.extensions.code === 'UNAUTHENTICATED';
  });
  if (unauthenticatedErr) {
    removeAuthToken();
    history.push('/');
  }
};

/**
 * Converts BankAccountWithInstutitionGQLType data to displayable string.
 */
export const bankAccountToDisplayString = (
  bankAccount?: BankAccountWithInstitutionAndDesignationGQLType & {
    organizationName?: string;
  },
): string => {
  const DEFAULT_BANK_STRING = '';
  if (!bankAccount) {
    return DEFAULT_BANK_STRING;
  }
  return `${
    bankAccount.organizationName ? `${bankAccount.organizationName}: ` : ''
  } ${bankAccount.institutionName} (${bankAccount.accountNumber})`;
};

// syntactic sugar to guarantee payload is of type FundingRequest
export const getDeliverablesPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): DeliverablesPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'DeliverablesPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type DeliverablesPayload',
      typeguardName: 'withDeliverablesPayloadAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

export const getUpdatedDeliverableStatusPayloadV1WithAssert = (
  event: EventForRenderFragmentGQLType,
): UpdatedDeliverableStatusPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'UpdatedDeliverableStatusPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type UpdatedDeliverableStatusPayload',
      typeguardName: 'getUpdatedDeliverableStatusPayloadV1WithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

export const getInsertedDeliverableStatusPayloadV1WithAssert = (
  event: EventForRenderFragmentGQLType,
): InsertedDeliverablePayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'InsertedDeliverablePayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type InsertedDeliverablePayload',
      typeguardName: 'getInsertedDeliverableStatusPayloadV1WithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

export const getSubmittedDeliverableSubmissionPayloadV1WithAssert = (
  event: EventForRenderFragmentGQLType,
): SubmittedDeliverableSubmissionPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'SubmittedDeliverableSubmissionPayload',
    new commonError.TypeError({
      resourceType:
        'Payload is not of type SubmittedDeliverableSubmissionPayload',
      typeguardName: 'getSubmittedDeliverableStatusPayloadV1WithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type FundingRequest
export const getFundingRequestPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): FundingRequestPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'FundingRequestPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type FundingRequestPayload',
      typeguardName: 'withFundingRequestPayloadAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type FundingRequestComponent
export const getFundingRequestComponentPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): FundingRequestComponentPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'FundingRequestComponentPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type FundingRequestComponentPayload',
      typeguardName: 'withFundingRequestComponentPayloadAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type IngestionPayload
export const getIngestionPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): IngestionPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'IngestionPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type IngeestionPayload',
      typeguardName: 'getFundingRequestPayloadWithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type IngestionPayload
export const getVerificationPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): VerificationPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'VerificationPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type VerificationPayload',
      typeguardName: 'getVerificationPayloadWithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

/**
 * Returns a truncated filename
 */
export const truncateUploadFileName = (
  fileName: string | undefined,
  maxLength: number = 43,
) => {
  if (fileName) {
    const sideLength = Math.floor((maxLength - 3) / 2);
    return fileName.length > maxLength
      ? `${fileName.substring(0, sideLength)}...${fileName.substring(
          fileName.length - sideLength,
        )}`
      : fileName;
  }
  return '';
};

export const getGreetingForTime = () => {
  const currentHour = new Date().getHours();
  let greeting;
  if (currentHour < 12) {
    greeting = 'Good Morning';
  } else if (currentHour < 18) {
    greeting = 'Good Afternoon';
  } else {
    greeting = 'Good Evening';
  }

  return greeting;
};

export const displayValueOrEmpty = (value: Maybe<string>) => {
  return value || '-';
};

const isFinleyTestUserEmailRegex = /^test.*@finleycms\.com$/;

export const isFinleyTestUserEmail = (email: string) => {
  return isFinleyTestUserEmailRegex.test(email);
};

export const isFinleyAdminDomain = (domain: string): boolean => {
  return domain === commonUrl.getAdminDashboardHostname();
};

/**
 * Helper to format currency amounts based on currency code
 * @param amount currency amount
 * @param currencyCode currency code from Bank.Currency
 * @returns formatted currency amount
 */
export const formatCurrencyAmount = (
  amount: number,
  currencyCode: Bank.Currency,
) => {
  switch (currencyCode) {
    case Bank.Currency.MXN:
      return mxnNumberWithCommas(amount);
    case Bank.Currency.USD:
    case Bank.Currency.CAD:
      return dollarNumberWithCommas(amount);
    default:
      throw new Error('Did not register currency code format');
  }
};

export const fileExtensionToMimeTypeMap: Record<string, string> = {
  // office
  doc: 'application/msword',
  docx:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // data
  csv: 'text/csv',
  json: 'application/json',
  txt: 'text/plain',
  rtf: 'application/rtf',
  // general
  rar: 'application/vnd.rar',
  gzip: 'application/gzip',
  zip: 'application/zip',
  // images
  png: 'image/png',
  bmp: 'image/bmp',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

// @jendevelops think about updating to add general FileTypes type
export const getAcceptedFileTypesConfig = (acceptedFileTypes: string[]) => {
  const acceptedFileTypeConfig: Record<string, string[]> = {};
  _.reduce(
    acceptedFileTypes,
    (acc, fileType: string) => {
      if (!(fileType in fileExtensionToMimeTypeMap)) {
        Sentry.captureException(
          `Invalid file type of [${fileType}] configured`,
        );
        return acc;
      }
      const newAcc = acc;
      const currentHeaderValue = fileExtensionToMimeTypeMap[fileType];
      const newFileType = `.${fileType}`;
      if (!(currentHeaderValue in newAcc)) {
        newAcc[currentHeaderValue] = [`.${fileType}`];
        return newAcc;
      }
      if (_.includes(newAcc[currentHeaderValue], newFileType)) {
        return newAcc;
      }
      newAcc[currentHeaderValue] = [...currentHeaderValue, `.${fileType}`];
      return newAcc;
    },
    acceptedFileTypeConfig,
  );
  return acceptedFileTypeConfig;
};

export const isCustomerDashboard = () =>
  window.location.origin === commonUrl.getCustomerDashboardUrl();

export const isAdminDashboard = () =>
  window.location.origin === commonUrl.getAdminDashboardUrl();

export const displayBaseFieldValue = (
  baseFieldConfig: CreditAgreement.DataTapeField.BaseConfig,
) => {
  const { type } = baseFieldConfig;
  switch (type) {
    case 'currency':
    case 'optionalCurrency':
      return (value: any) =>
        _.isNumber(value)
          ? formatCurrencyAmount(value, baseFieldConfig.currencyCode)
          : '-';
    case 'boolean':
    case 'optionalBoolean':
      return (value: any) => (_.isBoolean(value) ? String(value) : '-');
    case 'date':
    case 'optionalDate':
      return (value: any) =>
        value
          ? DateTime.fromFormat(
              value,
              baseFieldConfig.externalDateFormat,
            ).toLocaleString(DateTime.DATE_FULL)
          : '-';
    case 'dateTime':
    case 'optionalDateTime':
      return (value: any) =>
        value
          ? DateTime.fromFormat(value, baseFieldConfig.externalDateTimeFormat)
              .setZone(baseFieldConfig.defaultTz)
              .toLocaleString(DateTime.DATETIME_FULL)
          : '-';
    case 'percentage':
    case 'optionalPercentage':
      return (value: any) =>
        _.isNumber(value) ? numberToPercentageLabel(value) : '-';
    case 'float':
    case 'integer':
    case 'optionalFloat':
    case 'optionalInteger':
      return (value: any) => (_.isNumber(value) ? value : '-');

    case 'string':
    case 'optionalString':
    default:
      return (value: any) => (_.isString(value) ? value : '-');
  }
};

export const formatBaseFieldValue = (
  value: any,
  baseFieldType: CreditAgreement.DataTapeField.Type,
) => {
  if (value === null || value === undefined || value === 'undefined') {
    return '-';
  }
  if (baseFieldType === 'date' || baseFieldType === 'optionalDate') {
    return commonDate.dateToHumanDateString(DateTime.fromISO(value).toJSDate());
  }
  if (baseFieldType === 'currency' || baseFieldType === 'optionalCurrency') {
    return commonUtils.numberToCurrencyWithSymbol({
      number: Number(value),
    });
  }
  if (
    baseFieldType === 'percentage' ||
    baseFieldType === 'optionalPercentage'
  ) {
    return numberToPercentageLabel(Number(value));
  }
  if (baseFieldType === 'float' || baseFieldType === 'optionalFloat') {
    return Number(value).toFixed(2);
  }

  return String(value);
};

export const toDashOnNullish = (value: Maybe<any>) => {
  return value ?? '-';
};

export const scrollToTopOfElement = (elementId: string) => {
  document.getElementById(elementId)?.scroll({ top: 0 });
};
